import React, { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, Box, Divider, Link, Paper, Stack, Typography } from '@mui/material';
import BrandMark from './BrandMark';
import { BRAND } from '../theme';
import { LEGAL, LEGAL_PLACEHOLDERS_REMAIN } from '../legal';

export interface LegalSection {
  id: string;
  title: string;
  body: React.ReactNode;
}

/** Public page shell for the Privacy Policy and Terms of Service. */
const LegalPage: React.FC<{ title: string; intro: React.ReactNode; sections: LegalSection[] }> = ({
  title,
  intro,
  sections,
}) => {
  const location = useLocation();

  useEffect(() => {
    document.title = `${title} — ${BRAND.name}`;
    // Deep links such as /privacy#delete-your-data: the section only exists
    // after render, so the browser's own jump on load misses it.
    const target = location.hash && document.getElementById(location.hash.slice(1));
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [title, location.hash]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 2, sm: 5 }, px: 2 }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 820,
          mx: 'auto',
          p: { xs: 2.5, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 4,
        }}
      >
        <Link component={RouterLink} to="/" underline="none" color="inherit">
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 4 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'primary.main',
                color: '#fff',
              }}
            >
              <BrandMark fontSize="small" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {BRAND.name}
            </Typography>
          </Stack>
        </Link>

        {LEGAL_PLACEHOLDERS_REMAIN && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Draft: operator details in <code>src/legal.ts</code> are still placeholders. Fill them in and have
            this document reviewed before publishing.
          </Alert>
        )}

        <Typography variant="h4" component="h1" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Last updated: {LEGAL.lastUpdated}
        </Typography>

        <Box sx={{ '& p': { mb: 1.5, lineHeight: 1.7 }, '& li': { mb: 0.75, lineHeight: 1.7 } }}>
          {intro}

          <Box component="nav" aria-label="Contents" sx={{ my: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Contents
            </Typography>
            <Box component="ol" sx={{ m: 0, pl: 3, columns: { sm: 2 }, columnGap: 4 }}>
              {sections.map((s) => (
                <li key={s.id}>
                  <Link href={`#${s.id}`} underline="hover">
                    {s.title}
                  </Link>
                </li>
              ))}
            </Box>
          </Box>

          {sections.map((s, i) => (
            <Box component="section" key={s.id} id={s.id} sx={{ scrollMarginTop: 16, mt: 4 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                {i + 1}. {s.title}
              </Typography>
              {s.body}
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 4 }} />
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <Link component={RouterLink} to="/privacy" underline="hover" variant="body2">
            Privacy Policy
          </Link>
          <Link component={RouterLink} to="/terms" underline="hover" variant="body2">
            Terms of Service
          </Link>
          <Link component={RouterLink} to="/" underline="hover" variant="body2">
            Back to {BRAND.name}
          </Link>
        </Stack>
      </Paper>
    </Box>
  );
};

export default LegalPage;
